import { useParams } from "react-router-dom";

import { useDataset, useDeletion } from "../api/queries";
import { isStorageProblem, StorageProblem } from "../components/StorageProblem";
import { PageHeader } from "../components/layout/PageHeader";
import { ServerLines } from "../components/ui/ServerLines";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { formatDateTime, pluralize } from "../lib/format";

/**
 * The printable receipt: the page a client is forwarded.
 *
 * Every sentence on it was written by the server, on the side that knows what
 * was destroyed and what was checked afterwards. This page prints them
 * verbatim and adds no arithmetic of its own — including the last line, which
 * nobody enjoys writing and which is exactly why it is there.
 *
 * Returns:
 *     The receipt, or the honest reason there is not one.
 */
export function ReceiptPage(): JSX.Element {
  const { id = "" } = useParams();
  const dataset = useDataset(id);
  const deletion = useDeletion(id);

  const header = (
    <PageHeader
      eyebrow="Receipt"
      title={
        dataset.data
          ? `${dataset.data.account.name} · ${dataset.data.name}`
          : "Deletion receipt"
      }
      back={{ to: `/datasets/${id}`, label: "Back to the dataset" }}
      actions={
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => window.print()}
        >
          Print
        </button>
      }
    />
  );

  if (deletion.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading the receipt…" />
      </>
    );
  }

  if (isStorageProblem(deletion.error)) {
    return (
      <>
        {header}
        <StorageProblem
          error={deletion.error}
          onRetry={() => void deletion.refetch()}
        />
      </>
    );
  }

  if (deletion.isError) {
    return (
      <>
        {header}
        <ErrorBlock
          error={deletion.error}
          onRetry={() => void deletion.refetch()}
        />
      </>
    );
  }

  const receipt = deletion.data.receipt;

  if (!receipt) {
    return (
      <>
        {header}
        <p className="state-block">
          There is no receipt yet. The erasure was asked for{" "}
          {formatDateTime(deletion.data.requested_at)} by{" "}
          {deletion.data.requested_by}, and the console writes a receipt only
          after it has checked, in both regions, that every object is gone. A
          receipt it could not verify would be a lie with letterhead.
        </p>
        <ServerLines lines={deletion.data.lines} />
      </>
    );
  }

  return (
    <>
      {header}

      <article className="receipt">
        <ServerLines lines={receipt.lines} className="receipt__lines" />

        <dl className="facts">
          <div className="facts__item">
            <dt>Asked for by</dt>
            <dd>{receipt.requested_by}</dd>
          </div>
          <div className="facts__item">
            <dt>Asked for at</dt>
            <dd>{formatDateTime(receipt.requested_at)}</dd>
          </div>
          <div className="facts__item">
            <dt>Finished at</dt>
            <dd>{formatDateTime(receipt.completed_at)}</dd>
          </div>
          <div className="facts__item">
            <dt>How</dt>
            <dd>
              {receipt.immediate
                ? "straight away, at the client's request"
                : "after the day it was held for, in case somebody changed their mind"}
            </dd>
          </div>
        </dl>

        <p className="receipt__reason">Reason recorded: {receipt.reason}</p>

        <h2 className="section__title">
          {pluralize(receipt.objects.length, "object")} destroyed, across{" "}
          {receipt.regions.join(" and ")}
        </h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Where</th>
              <th scope="col">What</th>
              <th scope="col">Destroyed</th>
              <th scope="col">Checked gone</th>
            </tr>
          </thead>
          <tbody>
            {receipt.objects.map((object) => (
              <tr key={`${object.bucket}-${object.key}-${object.version_id}`}>
                <td className="mono">
                  {object.region}
                  <span className="admin-table__sub">{object.bucket}</span>
                </td>
                <td className="mono admin-table__wrap">
                  {object.key}
                  <span className="admin-table__sub">{object.version_id}</span>
                </td>
                <td className="admin-table__muted">
                  {formatDateTime(object.deleted_at)}
                </td>
                <td className="admin-table__muted">
                  {formatDateTime(object.absent_confirmed_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="section__title">Checksums of what was destroyed</h2>
        <ul className="checksums">
          {receipt.checksums_destroyed.map((checksum) => (
            <li className="mono" key={checksum}>
              {checksum}
            </li>
          ))}
        </ul>
      </article>
    </>
  );
}
